import { memo, useMemo } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

import { Table } from '@/_shared/components/Table';
import { Callout } from '@/_shared/components/ui/callout';
import { agentMarkdownSanitizeSchema, hardenAgentUrl } from '@/_shared/lib/agent-markdown';
import {
  deriveColumns,
  extractMarkdownTable,
  readMarkdownTableTitle,
  remarkPromoteTableTitles,
} from '@/_shared/lib/table';

import { useSyntaxStyle } from '../hooks/useSyntaxStyle';
import { safeAgentMarkdownHref, safeHttpsUrl } from '../lib/safe-url';
import { SyntaxHighlighter } from '../lib/syntax';

function MarkdownTable({ node }: { node?: unknown }): React.ReactElement | null {
  const extracted = useMemo(() => extractMarkdownTable(node), [node]);
  const columns = useMemo(() => (extracted ? deriveColumns(extracted.rows, extracted.headers) : []), [extracted]);
  const title = useMemo(() => readMarkdownTableTitle(node), [node]);

  if (!extracted) return null;

  return (
    <Table
      className="my-3"
      data={extracted.rows}
      columns={columns}
      title={title}
      enableCsvExport
      hardenCsvExport
      enableSort
      showRowCount
    />
  );
}

interface StreamingTextProps {
  content: string;
  /**
   * Renderers that replace the defaults for their elements (an assist note's blanks as chips). Pass a
   * module-level object: a fresh one each render defeats the memo.
   */
  components?: Components;
}

/**
 * Memoized on `content` — every SSE event re-renders the whole transcript, and without this each message
 * re-parses its markdown into a fresh AST every time. That churn is also what made scroll-restoring
 * children (the Table below) reset: their `data` arrived with a new identity on every unrelated render.
 */
export const StreamingText = memo(function StreamingText({
  content,
  components,
}: StreamingTextProps): React.ReactElement {
  const syntaxStyle = useSyntaxStyle();
  return (
    <div className="text-sm">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkPromoteTableTitles]}
        rehypePlugins={[[rehypeSanitize, agentMarkdownSanitizeSchema]]}
        urlTransform={hardenAgentUrl}
        components={{
          code({ className, children, ...props }) {
            const isInline = !className;
            if (isInline) {
              return (
                <code className="rounded bg-muted px-1.5 py-0.5 text-[0.8125rem] text-brand" {...props}>
                  {children}
                </code>
              );
            }
            const language = className?.replace('language-', '') ?? '';
            return (
              <SyntaxHighlighter
                language={language}
                style={syntaxStyle}
                customStyle={{
                  margin: 0,
                  padding: '1rem',
                  fontSize: '0.8125rem',
                  lineHeight: '1.625',
                }}
              >
                {String(children as string).replace(/\n$/, '')}
              </SyntaxHighlighter>
            );
          },
          pre({ children }) {
            return <pre className="my-3 overflow-x-auto rounded border border-border bg-muted">{children}</pre>;
          },
          img({ src, alt, title }) {
            // Own the scheme allow-list at the sink; do not rely on upstream sanitization alone (AI-1065).
            const safeSrc = safeHttpsUrl(src);
            if (!safeSrc) {
              return null;
            }
            return <img src={safeSrc} alt={alt ?? ''} title={title} className="max-w-full" />;
          },
          a({ children, href }) {
            // Own the scheme allow-list at the sink; do not rely on upstream sanitization alone (AI-1065).
            const safeHref = safeAgentMarkdownHref(href);
            if (!safeHref) {
              return <>{children}</>;
            }
            return (
              <a
                href={safeHref}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand underline decoration-brand/40 hover:decoration-brand"
              >
                {children}
              </a>
            );
          },
          table({ node }) {
            return <MarkdownTable node={node} />;
          },
          ul({ children }) {
            return <ul className="my-2 list-disc space-y-1 pl-6">{children}</ul>;
          },
          ol({ children }) {
            return <ol className="my-2 list-decimal space-y-1 pl-6">{children}</ol>;
          },
          blockquote({ children }) {
            // Keep the semantic <blockquote> (screen readers, copy/paste) and style it as a Callout.
            return (
              <blockquote className="my-3">
                <Callout variant="highlight">{children}</Callout>
              </blockquote>
            );
          },
          h1({ children }) {
            return <h1 className="mt-4 mb-2 text-lg font-bold text-foreground">{children}</h1>;
          },
          h2({ children }) {
            return <h2 className="mt-3 mb-2 text-base font-bold text-foreground">{children}</h2>;
          },
          h3({ children }) {
            return <h3 className="mt-3 mb-1 text-sm font-bold text-foreground">{children}</h3>;
          },
          p({ children }) {
            return <p className="my-2 leading-relaxed first:mt-0 last:mb-0">{children}</p>;
          },
          ...components,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
});
